import { Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TableBalanceComponent } from "@app/shared/components/table-balance/table-balance.component";
import { TableDreCompleteComponent } from "@app/shared/components/table-dre-complete/table-dre-complete.component";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { PdfService } from "@app/shared/services/pdf.service";
import { ReportBeachmarkingSectionComponent } from "@app/views/auth/report/components/report-beachmarking-section/report-beachmarking-section.component";
import { ReportHeaderComponent } from "@app/views/auth/report/components/report-header/report-header.component";
import { ReportPdfFooterComponent } from "@app/views/auth/report/components/report-pdf-footer/report-pdf-footer.component";
import { ReportPdfIndexComponent } from "@app/views/auth/report/components/report-pdf-index/report-pdf-index.component";
import { ReportPerformanceComponent } from "@app/views/auth/report/components/report-performance/report-performance.component";
import { ReportTableValuesPdfComponent } from "@app/views/auth/report/components/report-table-values-pdf/report-table-values-pdf.component";
import { ReportToolbarComponent } from "@app/views/auth/report/components/report-toolbar/report-toolbar.component";
import { AboutVisualizationComponent } from "@app/views/auth/report/components/report-visualization-explore/about-visualization/about-visualization.component";
import { ReportVisualizationNoteComponent } from "@app/views/auth/report/components/report-visualization-note/report-visualization-note.component";
import { ReportVisualizationComponent } from "@app/views/auth/report/components/report-visualization/report-visualization.component";
import { ReportPreferenceService } from "@arch/domain/application/services";
import { DRESerieData } from "@arch/domain/application/usecase";
import { CompanyEntity, ReportEntity } from "@arch/domain/entity";
import {
    GetCompanyController,
    GetReportController,
    GetScenariosController,
    PopulateReportController
} from "@arch/presentation/controllers";
import { GetDreDataController } from "@arch/presentation/controllers/get-dre-data.controller";

@Component({
    selector: "app-pdf-dre",
    imports: [
        SharedMaterialModule,
        SharedDsnModule,
        ReportHeaderComponent,
        ReportBeachmarkingSectionComponent,
        ReportToolbarComponent,
        ReportVisualizationComponent,
        ReportTableValuesPdfComponent,
        AboutVisualizationComponent,
        ReportPdfFooterComponent,
        ReportPdfIndexComponent,
        ReportVisualizationNoteComponent,
        ReportPerformanceComponent,
        TableBalanceComponent,
        TableDreCompleteComponent
    ],
    templateUrl: "./pdf.component.html",
    styles: [
        `
            .divider {
                border-bottom: var(--border-width-1) solid
                    var(--color-border-light);
            }
            .note-container {
                padding: var(--spacing-8);
                background-color: var(--color-expand-collapse-notes-bg);
                border-radius: var(--border-radius-8);
                gap: var(--spacing-8);
                .fa-sticky-note {
                    font-size: var(--font-size-12);
                }
            }
            .dre-table-wrapper {
                gap: var(--spacing-16);
                padding: var(--spacing-12);
                border: var(--border-width-1) solid var(--color-border-light);
                border-radius: var(--border-radius-8);
                width: 100%;
            }
            .pdf-section-about-visualization {
                margin-top: var(--spacing-8);
            }
        `
    ],
    styleUrls: ["../../pdf-config.scss"]
})
export class PdfDreComponent implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly getReportController = inject(GetReportController);
    private readonly getCompanyController = inject(GetCompanyController);
    private readonly getScenariosController = inject(GetScenariosController);
    private readonly populateReportController = inject(
        PopulateReportController
    );
    private readonly getDreDataController = inject(GetDreDataController);
    public pdfService = inject(PdfService);
    public reportPreferenceService = inject(ReportPreferenceService);
    public report: ReportEntity;
    public company: CompanyEntity;
    public loading: boolean = true;
    public hasBenchmarking: boolean = true;
    public tableDrePages: {
        dates?: Date[];
        indexInterval: number[];
        tabActive: "period" | "accumulated";
    }[] = [];
    public tableBalancePages: { assets: number[]; liabilities: number[] }[] = [
        {
            assets: Array.from({ length: 13 }, (_, i) => i),
            liabilities: Array.from({ length: 13 }, (_, i) => i)
        },
        {
            assets: Array.from({ length: 10 }, (_, i) => i + 13),
            liabilities: Array.from({ length: 10 }, (_, i) => i + 13)
        }
    ];
    public indexTables: {
        _id: string;
        name: string;
        type: string;
        sourceLabel?: string;
    }[] = [];

    async ngOnInit(): Promise<void> {
        this.loading = true;
        const { companyId, reportId } = this.route.snapshot.queryParams;
        if (!companyId || !reportId) {
            this.loading = false;
            this.router.navigate(["/"]);
            return;
        }
        const report = await this.getReportController.execute({
            reportId: reportId
        });
        this.setIndexTables(report);
        this.reportPreferenceService.setAll(report.props?.optionsContent);
        const [company, scenarios] = await Promise.all([
            this.getCompanyController.execute({
                companyId: companyId,
                options: {
                    isGroup: report.props.isGroup
                }
            }),
            this.getScenariosController.execute({
                companyId,
                options: {
                    addOriginal: true,
                    addProjection: report.props.isGroup ? false : true
                }
            })
        ]);
        report.setScenarioOptions(scenarios);
        this.company = company;
        this.report = report;
        this.pdfService.config(this.report);
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: this.report,
            options: {
                performance: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true,
                dre: true,
                balance: this.report.hasBalance
            }
        });
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        await this.setTableDrePages();
        this.loading = false;
        this.pdfService.print();
    }

    private setIndexTables(report: ReportEntity): void {
        if (report.props.sources === "accounting") {
            this.indexTables = [
                {
                    _id: "TableBalance",
                    name: "Balanço",
                    type: "table_dre"
                },
                {
                    _id: "TableDre",
                    name: "DRE",
                    type: "table_dre"
                }
            ];
        } else {
            this.indexTables = [
                {
                    _id: "TableDre",
                    name: "DRE",
                    type: "table_dre"
                }
            ];
        }
    }
    private async setTableDrePages(): Promise<void> {
        const dreSeriesData = await this.getDreData();
        const seriesDataChunks = this.chunkSeriesData(
            dreSeriesData.map((data, index) => index),
            this.report.hasBalance ? 20 : 17
        );
        for (const dates of this.pdfService.tableValuesDates) {
            for (const seriesData of seriesDataChunks) {
                this.tableDrePages.push({
                    dates,
                    indexInterval: seriesData,
                    tabActive: "period"
                });
            }
        }
        if (!this.report.options.comparation.active) {
            for (const seriesData of seriesDataChunks) {
                this.tableDrePages.push({
                    indexInterval: seriesData,
                    tabActive: "accumulated"
                });
            }
        }
    }

    private async getDreData(): Promise<DRESerieData[]> {
        return await this.getDreDataController.execute({
            options: this.report.options,
            company: this.company,
            dates: [],
            optionsConfig: {
                verticalAnalysisReference:
                    this.report.options.dre.verticalReference,
                calcAnalysisVertical: this.report.options.dre.analysisVertical,
                calcAnalysisHorizontal:
                    this.report.options.dre.analysisHorizontal
            }
        });
    }

    private chunkSeriesData(
        seriesData: number[],
        chunkSize: number
    ): number[][] {
        if (!seriesData || !Array.isArray(seriesData)) return [];
        const seriesDataChunks: number[][] = [];
        for (let i = 0; i < seriesData.length; i += chunkSize) {
            seriesDataChunks.push(seriesData.slice(i, i + chunkSize));
        }
        return seriesDataChunks;
    }

    public onBenchmarkingDataLoaded(hasBenchmarkingData: boolean): void {
        this.hasBenchmarking = hasBenchmarkingData;
    }
}
